const OFFLINE_RESULT_TOAST_MIN_GAME_ELAPSED_MS = 15 * 60_000;
const OFFLINE_RESULT_MODAL_MIN_GAME_ELAPSED_MS = 60 * 60_000;

export type OfflineRewardPresentation = "none" | "toast" | "modal";

export function resolveOfflineRewardPresentation(
  appliedGameElapsedMs: number,
): OfflineRewardPresentation {
  if (!Number.isFinite(appliedGameElapsedMs) || appliedGameElapsedMs < 0) {
    throw new RangeError(
      "appliedGameElapsedMs must be a finite non-negative number",
    );
  }
  if (appliedGameElapsedMs < OFFLINE_RESULT_TOAST_MIN_GAME_ELAPSED_MS) {
    return "none";
  }
  if (appliedGameElapsedMs < OFFLINE_RESULT_MODAL_MIN_GAME_ELAPSED_MS) {
    return "toast";
  }
  return "modal";
}

export type OfflineRewardPresentationDispatchResult =
  | {
      readonly status: "dispatched";
      readonly presentation: OfflineRewardPresentation;
    }
  | { readonly status: "duplicate" };

export interface OfflineRewardPresentationDispatcher<TModel> {
  dispatch(candidate: {
    readonly rewardId: string;
    readonly appliedGameElapsedMs: number;
    readonly createModel: () => TModel;
  }): OfflineRewardPresentationDispatchResult;
}

export function createOfflineRewardPresentationDispatcher<TModel>(options: {
  readonly showToast: (model: TModel) => void;
  readonly showModal: (model: TModel) => void;
}): OfflineRewardPresentationDispatcher<TModel> {
  const dispatchedRewardIds = new Set<string>();

  return {
    dispatch(candidate) {
      if (dispatchedRewardIds.has(candidate.rewardId)) {
        return { status: "duplicate" };
      }

      const presentation = resolveOfflineRewardPresentation(
        candidate.appliedGameElapsedMs,
      );
      if (presentation === "toast") {
        options.showToast(candidate.createModel());
      } else if (presentation === "modal") {
        options.showModal(candidate.createModel());
      }
      dispatchedRewardIds.add(candidate.rewardId);
      return { status: "dispatched", presentation };
    },
  };
}
